<template>
    <div class="EntityNameMatching" :class="{ 'EntityNameMatching--globalScope': showGlobalScope }">
        <div class="EntityNameMatching__form">
            <GlobalActionScopeCard
                v-if="showGlobalScope"
                class="EntityNameMatching__scopeCard"
                :entity-count="affectedEntityCount"
                :field-label="fieldLabel"
            />
            <AcceptanceReviewContent
                :is-global-type="isGlobalType"
                :field-name="fieldName"
                :actual-value="value"
                :required-value="actionDetails.comparedTo"
                :align-top="actionReviewHasEntityNames(actionReview)"
                :read-only="readOnly"
                :outcome="recordedOutcome"
                @accept="resolve(true)"
                @reject="resolve(false)"
            >
                <template
                    v-if="actionReviewHasEntityNames(actionReview)"
                    #requiredValue
                >
                    <EntityNameMatchingEntityNames
                        :entity-names="actionReview.entityNames"
                    />
                </template>
            </AcceptanceReviewContent>
        </div>
        <div
            v-if="!isGlobalType"
            class="EntityNameMatching__documents"
        >
            <LogoLoader v-if="loading" />
            <DocumentsViewer v-if="!loading" :documents="docsStatus.list" />
        </div>
        <RbacDisplayGuard v-else-if="showGlobalScope" :features="[RbacFeature.actionsViewDocument]">
            <div class="EntityNameMatching__documents">
                <GlobalActionDocumentPanel :action-review="actionReview" />
            </div>
        </RbacDisplayGuard>
    </div>
</template>

<script lang="ts">
    import { Component, Prop, Vue, Watch } from '@evidentid/vue-property-decorator';

    import startCase from 'lodash/startCase';

    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    import { Alert } from '@evidentid/dashboard-commons/components/Alert';
    import { LogoLoader } from '@evidentid/dashboard-commons/components/LogoLoader';
    import { Cell, HeadCell, Row, Table } from '@evidentid/dashboard-commons/components/Table';
    import RbacDisplayGuard
        from '@evidentid/dashboard-commons/modules/role-base-access-control/components/rbac-guard/RbacDisplayGuard.vue';
    import { typeNarrowing } from '@evidentid/dashboard-commons/utils/typeNarrowing';
    import {
        AcceptanceActionResolveInput,
        ActionReview,
        ActionType,
        EntityNameMatchingActionDetails,
    } from '@evidentid/tprm-portal-lib/models/entity-actions-review';
    import { RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac';
    import {
        actionReviewHasEntityNames,
    } from '@evidentid/tprm-portal-lib/utils/action-review-has-entity-names/actionReviewHasEntityNames';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import NewButton from '@/components/new-button/NewButton.vue';
    import AcceptanceReviewContent
        from '@/modules/entity-actions-review/components/AcceptanceReviewContent/AcceptanceReviewContent.vue';
    import DocumentsViewer from '@/modules/entity-actions-review/components/DocumentsViewer/DocumentsViewer.vue';
    import EmptyValueDashSymbol
        from '@/modules/entity-actions-review/components/EmptyValueDashSymbol/EmptyValueDashSymbol.vue';
    import GlobalActionDocumentPanel
        from '@/modules/entity-actions-review/components/GlobalActionDocumentPanel/GlobalActionDocumentPanel.vue';
    import GlobalActionScopeCard
        from '@/modules/entity-actions-review/components/GlobalActionScopeCard/GlobalActionScopeCard.vue';
    import { CoiDocumentStatus, useEntityActionsReviewStore } from '@/modules/entity-actions-review/store';
    import { EntityActionScopeType } from '@/modules/entity-actions-review/types';
    import { getEntityMatchFieldName } from '@/modules/entity-actions-review/utils/getActionRequired';
    import { getActionScopeType } from '@/modules/entity-actions-review/utils/getActionScopeType';
    import { getAffectedEntityCount } from '@/modules/entity-actions-review/utils/globalActionScope';

    import EntityNameMatchingEntityNames from './sub-components/EntityNameMatchingEntityNames.vue';

    @Component({
        emits: [ 'resolve' ],
        components: {
            AcceptanceReviewContent,
            EmptyValueDashSymbol,
            Alert,
            NewButton,
            Cell,
            FontAwesomeIcon,
            HeadCell,
            Row,
            Table,
            EntityNameMatchingEntityNames,
            DocumentsViewer,
            GlobalActionDocumentPanel,
            GlobalActionScopeCard,
            LogoLoader,
            RbacDisplayGuard,
        },
    })
    export default class EntityNameMatching extends Vue {
        @Prop({ type: Object })
        private actionReview!: ActionReview;

        @Prop({ type: Boolean, default: false })
        private readOnly!: boolean;

        private RbacFeature = RbacFeature;

        private get recordedOutcome(): 'accepted' | 'rejected' | null {
            return this.actionReview.resolution?.outcome ?? null;
        }

        private get actionDetails(): EntityNameMatchingActionDetails {
            return typeNarrowing<EntityNameMatchingActionDetails>(
                this.actionReview.action,
                this.actionReview.action.$action === ActionType.entityNameMatchingV1,
                'The actionReview object of this component has incorrect data, this is likely caused by mistake in the code.',
            );
        }

        private get fieldName(): string {
            return startCase(getEntityMatchFieldName(this.actionDetails));
        }

        private get value(): any {
            return this.actionDetails.given;
        }

        private get isGlobalType(): boolean {
            return getActionScopeType(this.actionDetails) === EntityActionScopeType.global;
        }

        private get affectedEntityCount(): number {
            return getAffectedEntityCount(this.actionReview.evaluationResults);
        }

        private get showGlobalScope(): boolean {
            return this.isGlobalType && !this.readOnly && this.affectedEntityCount > 0;
        }

        private get fieldLabel(): string {
            return this.fieldName.toLowerCase();
        }

        private get rpName(): string {
            return this.$rp.current!;
        }

        private get docsStatus(): CoiDocumentStatus {
            if (useEntityActionsReviewStore().rprDocuments[this.rpName] &&
                useEntityActionsReviewStore().rprDocuments[this.rpName][this.actionReview.id]) {
                return useEntityActionsReviewStore().rprDocuments[this.rpName][this.actionReview.id];
            } else {
                return { status: OperationStatus.uninitialized, list: [] };
            }
        }

        private get loading(): boolean {
            return this.docsStatus.status === OperationStatus.loading;
        }

        private resolve(accepted: boolean): void {
            const data: AcceptanceActionResolveInput = {
                $action: ActionType.entityNameMatchingV1,
                accepted,
            };
            this.$emit('resolve', data);
        }

        private actionReviewHasEntityNames = actionReviewHasEntityNames;

        @Watch('actionReview', { immediate: true })
        private onActionReviewChange(): void {
            // GlobalActionDocumentPanel loads the documents of the one entity it shows.
            if (this.isGlobalType) {
                return;
            }
            if (this.docsStatus.status === OperationStatus.uninitialized &&
                this.actionReview.evaluationResults?.length > 0
            ) {
                // Get unique combinations of verificationId, requirementType and instanceId from evaluation results.
                // The instanceId is required to find the documents of multi-instance requirements (e.g. credentials).
                const documentsFor = this.actionReview.evaluationResults.map((result) => ({
                    verificationId: result.verificationId,
                    requirementType: result.coverageType,
                    instanceId: result.instanceId,
                }));
                const uniqueDocumentsFor = documentsFor.filter((doc, index) =>
                    documentsFor.findIndex((d) =>
                        d.verificationId === doc.verificationId &&
                        d.requirementType === doc.requirementType &&
                        d.instanceId === doc.instanceId,
                    ) === index,
                );

                useEntityActionsReviewStore().loadRprDocuments({
                    rpName: this.rpName,
                    actionId: this.actionReview.id,
                    documentsFor: uniqueDocumentsFor,
                });
            }
        }
    }
</script>
